import { useState } from "react";
import { Link2, Twitter, Linkedin, Facebook } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact share row for public card detail pages.
 * Renders below the hero blurb; visually subordinate to hero typography.
 */
export default function ShareRow({ title = "", className = "" }) {
  const [copying, setCopying] = useState(false);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(title);

  const onCopy = async () => {
    if (!url) return;
    setCopying(true);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { duration: 2500 });
    } catch {
      toast.error("Couldn't copy. Long-press the URL bar instead.");
    } finally {
      setTimeout(() => setCopying(false), 600);
    }
  };

  const tweetHref = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const iconBtn =
    "inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white/85 transition-colors hover:bg-white/20 hover:text-white";

  return (
    <div
      data-testid="share-row"
      className={`flex items-center gap-2 ${className}`}
    >
      <button
        type="button"
        onClick={onCopy}
        disabled={copying}
        data-testid="share-copy-link"
        className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 h-9 text-[12px] font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/25 disabled:opacity-60"
      >
        <Link2 className="h-3.5 w-3.5" />
        {copying ? "Copying…" : "Copy link"}
      </button>
      <a
        href={tweetHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-twitter"
        aria-label="Share on X"
        className={iconBtn}
      >
        <Twitter className="h-4 w-4" />
      </a>
      <a
        href={linkedinHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-linkedin"
        aria-label="Share on LinkedIn"
        className={iconBtn}
      >
        <Linkedin className="h-4 w-4" />
      </a>
      <a
        href={facebookHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-facebook"
        aria-label="Share on Facebook"
        className={iconBtn}
      >
        <Facebook className="h-4 w-4" />
      </a>
    </div>
  );
}
